import React, { useMemo, useState } from "react";
import { FiCopy, FiCheck } from "react-icons/fi";
import { ERROR_CODES, PUBLIC_API_BASE, codeSamples } from "../../chatbot/chat";

const TABS = [["curl", "cURL"], ["javascript", "JavaScript"], ["python", "Python"], ["php", "PHP"], ["embed", "Website widget"]];
const ENDPOINTS = [
  ["POST", "/chat/", 'Ask a question: JSON {"message", "conversation_id"?, "stream"?, "speak"?} or a form with an "audio" file. Returns the answer, or the answer word by word.'],
  ["POST", "/speak/", "Turn text into spoken audio (MP3) in a voice of its language."],
  ["POST", "/transcribe/", "Turn a recording (WAV, MP3, FLAC, OGG) into text, in 20+ languages."],
  ["GET", "/bot/", "The chatbot's name, greeting and colour (what a chat window needs to start)."],
  ["GET", "/conversations/{id}/", "Read a conversation. DELETE the same address to forget it."],
  ["GET", "/usage/", "How many questions the chatbot answered today, this month and in total."],
];

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* the text can still be selected by hand */ }
  };
  return <button type="button" onClick={copy} className="absolute top-2.5 right-2.5 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs inline-flex items-center gap-1.5">{done ? <><FiCheck /> Copied</> : <><FiCopy /> Copy</>}</button>;
}

// What the owner gives a client: the address, how to send a question, and examples to copy. (The demo uses the widget key; a client gets a secret key of their own.)
export default function ApiDocs({ widgetKey }) {
  const [tab, setTab] = useState("curl");
  const samples = useMemo(() => codeSamples(PUBLIC_API_BASE, "YOUR_API_KEY", widgetKey || "YOUR_WIDGET_KEY"), [widgetKey]);
  return (
    <div className="space-y-8">
      <div>
        <div role="tablist" aria-label="Examples" className="flex flex-wrap gap-2 mb-3">
          {TABS.map(([key, label]) => (
            <button key={key} role="tab" aria-selected={tab === key} type="button" onClick={() => setTab(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${tab === key ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}>{label}</button>
          ))}
        </div>
        <div className="relative">
          <pre className="rounded-xl bg-black/40 border border-navy-border p-4 pr-24 overflow-x-auto text-[12.5px] leading-relaxed text-gray-200 whitespace-pre" tabIndex={0}><code>{samples[tab]}</code></pre>
          <CopyButton text={samples[tab]} />
        </div>
        {tab === "embed" && <p className="text-xs text-gray-400 mt-2">A bubble in the corner of the page that opens a chat with typing, voice questions and spoken answers. It only works on the websites you list for the chatbot.</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold mb-3">Endpoints <span className="text-xs text-gray-400 font-normal">(all under {PUBLIC_API_BASE}/core/chatbot/v1)</span></h4>
          <ul className="space-y-2.5 text-sm">
            {ENDPOINTS.map(([verb, path, about]) => (
              <li key={path} className="rounded-lg bg-white/5 border border-navy-border px-3.5 py-2.5">
                <div className="font-mono text-[12.5px]"><span className="text-accent-light font-bold mr-2">{verb}</span>{path}</div>
                <div className="text-gray-400 text-[12.5px] mt-0.5">{about}</div>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3">Good to know</h4>
          <ul className="space-y-2 text-sm text-gray-300 list-disc ml-5">
            <li>Every client gets a <b>secret API key</b> (<code>Authorization: Bearer ...</code>). It belongs on their server, never in a web page. Keys can be revoked at any time.</li>
            <li>A chat window on a website uses a <b>public widget key</b> that only works from the websites you allow.</li>
            <li>Send the <code>conversation_id</code> of an answer with the next question and the chatbot remembers the conversation.</li>
            <li>Give each chatbot its own <b>instructions</b>: the business, its facts, its tone. The AI answers by itself from them and from what it knows - there is no list of ready answers.</li>
            <li>Each chatbot has limits (per minute, per day), so one client cannot use up the others' share.</li>
          </ul>
          <h4 className="font-semibold mt-6 mb-3">Errors</h4>
          <ul className="space-y-2 text-[12.5px]">
            {ERROR_CODES.map(([code, about]) => <li key={code}><span className="font-mono text-amber-200">{code}</span><span className="text-gray-400"> - {about}</span></li>)}
          </ul>
          <p className="text-xs text-gray-500 mt-3">Errors look like {'{ "error": { "code": "...", "message": "..." } }'}.</p>
        </div>
      </div>
    </div>
  );
}
